import React from "react";
import { useSelector } from "react-redux";
import { CraftFormProvider, FormRenderer } from "@craft-form/renderer";
import useCraftDataProvider from "../hook/useCraftDataProvider";
import useProposalBlueprint from "../hook/useProposalBlueprint";
import craftFormTheme from "../../../utils/craftFormTheme";

// The JSON-driven proposal form, rendered by @craft-form/renderer from the
// blueprint the backend publishes — publishedformlayout / publishedformfields
// / publishedrules, fetched by getFormRenderer into the buyNow slice and read
// here via useProposalBlueprint. Mirrors the admin panel's ProposalForm.
// ./JSON.js is no longer imported; it survives only as a reference copy of the
// shape the DB is expected to serve.
//
// `methods` is owned by MembersDetails (not created here) so the page's
// "Save Draft" and its own Previous/Next/Finish buttons can read and validate
// this form's live values — @craft-form/renderer's `methods` prop exists
// precisely to share one react-hook-form instance with a host.
//
// showNavButtons={false} hides the renderer's built-in Next/Back/Finish so the
// page can render this project's own bootstrap buttons instead; the step is
// then driven from the host via activeStep/setActiveStep (the renderer
// supports being step-controlled, but only when BOTH are supplied — passing
// just one silently falls back to its internal step state). Navigation
// semantics live in ../hook/useProposalWizard. The wizard still renders its
// own stepper/progress indicator.
//
// `onSave` is only supplied on the renderer:1 path, where Finish is driven by
// the library's own finish() — which is a no-op without it. On the legacy path
// it stays undefined: MembersDetails submits via handleSubmit itself there, so
// the renderer never needs to call back.
//
// `children` render INSIDE CraftFormProvider, next to the form. That placement
// is load-bearing: it is what lets ProposalNavButtons reach the navigation
// state via useFormNavigation(), which only resolves under this provider.
const CraftProposalForm = ({
  methods,
  activeStep,
  setActiveStep,
  isEditing = false,
  onSave,
  children,
}) => {
  const customerData = useSelector((state) => state.common.customerData);
  const { fields, layout, rulesJson, isReady } = useProposalBlueprint();

  const dataProvider = useCraftDataProvider(
    customerData?.parenttype,
    customerData?.parentid,
  );

  // Hold the form back until customerData has actually landed. getCustomerInfo()
  // is fired from the header's mount effect, so on the first commit this is
  // still {} and parenttype/parentid are undefined — and the renderer's select
  // fetches its entity options in a mount effect that does NOT list
  // dataProvider in its dependencies. Mounting early therefore burns that one
  // and only fetch on a provider that throws "missing parentId", the select
  // falls back to its static placeholder options, and no later customerData
  // update can make it retry. Rendering nothing until the scoping values exist
  // keeps that single fetch for a provider that can actually serve it.
  const isCustomerDataReady =
    !!customerData && Object.keys(customerData).length > 0;
  if (!isCustomerDataReady) return null;

  // Same reasoning for the blueprint: getFormRenderer resolves independently of
  // getCustomerInfo, and the slice defaults are not the shapes FormRenderer
  // needs, so mounting early would hand it an empty layout and burn the select
  // fields' one mount-effect option fetch on a form with no fields yet.
  if (!isReady) return null;

  return (
    <CraftFormProvider
      config={{
        mode: "live",
        rulesEnabled: true,
        validationsEnabled: true,
      }}
      dataProvider={dataProvider}
      methods={methods}
      theme={craftFormTheme}
    >
      <FormRenderer
        fields={fields}
        layout={layout}
        rulesJson={rulesJson}
        activeStep={activeStep}
        setActiveStep={setActiveStep}
        showNavButtons={false}
        mode={isEditing ? "update" : "create"}
        onSave={onSave}
      />
      {children}
    </CraftFormProvider>
  );
};

export default CraftProposalForm;
